/**
 * RemoteProjectAdapter.ts — HTTP project client for the `/api/projects` surface.
 *
 * A **project** is a self-contained package: graph + manifest + code + optional
 * data/notebooks. This adapter talks to the backend's unified `/api/projects/*`
 * surface, replacing the older split between `RemoteLibraryAdapter` (saved
 * graphs) and `RemoteDocumentAdapter` (editing sessions).
 *
 * It implements the `LibraryAdapter` interface so the Composer's Library/Templates
 * tabs consume projects transparently — the entries are `LibraryEntry`-shaped,
 * derived from the richer `ProjectEntry`.
 *
 * The graph (ScenarioBundle) is fetched via `GET /api/projects/{id}/graph`.
 * Download (ZIP) + run controls + notebook are exposed as separate methods
 * (they don't fit the `LibraryAdapter` interface but the MenuBar needs them).
 */
import type { ChartEntry, ChartSpec, ScenarioBundle } from '../engine/types';
import type {
  ReplayDoc,
  ReplayIndexEntry,
  ReplayRunInfo,
  ReplayTickRow,
} from '../replay/types';
import {
  LibraryAdapter,
  LibraryEntry,
  LibraryError,
} from '../library/LibraryAdapter';
import { uuid } from '../library/ids';

/** A project entry — the backend's richer metadata shape (ProjectEntry in
 *  manifest.py). */
export interface ProjectEntry extends LibraryEntry {}

/** Run status from `GET /api/projects/{id}/run`. */
export interface ProjectRunStatus {
  project_id: string;
  running: boolean;
  pid: number | null;
  exit_code: number | null;
  /** The run-level agents kill-switch (absent on older backends = enabled).
   *  When false the relay path skips agent application; a fresh run/start
   *  resets it to true. */
  agentsEnabled?: boolean;
}

/** Notebook server status from `GET /api/projects/{id}/notebook`. */
export interface NotebookStatus {
  project_id: string;
  running: boolean;
  url: string | null;
  port: number | null;
  ready: boolean;
}

/** One spawned process tracked for a project. From `GET /api/projects/{id}/tasks`.
 *  The unified task list the Code tab's Tasks panel renders: the model runner
 *  (`kind:'model'`), the Jupyter notebook server (`kind:'notebook'`), trainer
 *  and model-runner rows, the image refresh — and `kind:'task'` rows for the
 *  manifest-declared custom tasks (builds, test runs). Read-only — start/stop
 *  stay on the dedicated routes. The kind is an open string: unknown kinds
 *  render as rows without controls. */
export interface ProjectTask {
  kind:
    | 'model'
    | 'notebook'
    | 'workflows'
    | 'trainer'
    | 'mlmodel'
    | 'images'
    | 'task'
    | (string & {});
  label: string;
  running: boolean;
  pid?: number | null;
  exit_code?: number | null;
  port?: number | null;
  url?: string | null;
  /** Status detail for the row's tooltip — the image-refresh row's error or
   *  last output line (kind === 'images' only). */
  detail?: string | null;
  /** The trainer module name (kind === 'trainer' only) — the start/stop
   *  routes are per-trainer (docs/TRAINING.md §5). */
  trainer?: string;
  /** The model module name (kind === 'mlmodel' only) — the run/stop routes
   *  are per-model (docs/TRAINING.md §7). */
  model?: string;
  /** The custom task's name (kind === 'task' only) — the start/stop/status
   *  routes are per-task, and the row's log view polls its output. */
  task?: string;
  /** Which protocol entries the module defines (kind === 'mlmodel' only),
   *  in score → train → evaluate preference order — the panel's Run button
   *  picks the first. */
  entries?: string[];
}

/** A manifest-declared custom task's status — the /tasks/{name} body. The
 *  `output` field carries the tailed stdout (last 4000 chars) for the Tasks
 *  panel's log view. */
export interface CustomTaskStatus {
  project_id: string;
  task: string;
  running: boolean;
  pid?: number | string | null;
  exit_code?: number | null;
  started_at?: string | null;
  output: string;
}

/** One project model from `GET /api/projects/{id}/models` — the AST-parsed
 *  registry index (a listing never imports model code). Drives the Tasks
 *  panel's model rows and the eval form's model picker. */
export interface ProjectModelInfo {
  name: string;
  label: string;
  description: string;
  /** Declared dataset refs (`table:<t>` or run ids) — what a run preloads
   *  when the caller doesn't override. */
  inputs: string[];
  /** Declared output tables (documentation for the machinery). */
  outputs: string[];
  /** Which of train/score/evaluate the module defines. */
  entries: string[];
}

/** One live Jupyter kernel for a project, joined with its notebook file name.
 *  From the `kernels` field of `GET /api/projects/{id}/tasks`. The Tasks panel
 *  renders these as a "Kernels" group beneath the Notebook row, each with an
 *  Interrupt button (`POST /notebook/kernels/{id}/interrupt`). `execution_state`
 *  is the authoritative busy/idle flag from the Jupyter Server REST API. */
export interface KernelInfo {
  id: string;
  name?: string | null;
  execution_state: string;
  last_activity?: string | null;
  connections?: number;
  /** Notebook path relative to the project root (e.g. `notebooks/demo.ipynb`),
   *  or null for a headless kernel with no session. */
  path?: string | null;
  /** Bare notebook file name (e.g. `demo.ipynb`), or null for a headless kernel. */
  file?: string | null;
}

/** A runnable source file under the project's `agents.codeRoot` (default
 *  `src/`). `module` is the dotted Python path that becomes `AgentConfig.target`
 *  (e.g. `src/agents/policy.py` → `agents.policy`). From `GET /api/projects/{id}/files`. */
export interface SourceFile {
  /** On-disk path relative to the project root (e.g. `src/economy.py`). */
  path: string;
  /** Dotted module path — the value for `AgentConfig.target`. */
  module: string;
  /** Bare filename (e.g. `economy.py`). */
  name: string;
}

/** A file or directory in the whole-project tree. From `GET /files?all=1`.
 *  Used by the Code tab's file-tree sidebar. `kind` drives both the tree icon
 *  and the editor surface: a `notebook` kind (.ipynb) renders via the Jupyter
 *  view, everything else via Monaco. Mirrors the backend's
 *  `{path, name, kind}` shape. */
export interface ProjectFile {
  /** On-disk path relative to the project root, POSIX-style (e.g. `src/economy.py`). */
  path: string;
  /** Bare name (e.g. `economy.py`, or `src` for a directory). */
  name: string;
  /** Coarse kind: `dir` for directories, `notebook` for `.ipynb`, else the
   *  Monaco language family. */
  kind: 'dir' | 'python' | 'json' | 'sql' | 'text' | 'notebook';
}

/** Plot registry entry — the Plot tab's left rail. AST-parsed from the module's
 *  `META` dict by the backend (no import). `id` is the filename stem. */
export interface PlotMeta {
  id: string;
  label: string;
  description: string;
  /** When the plot re-renders: `tick` on a throttled timer watching recent
   *  ticks, `ondemand` on a manual Refresh, `manual` once on mount. */
  refresh: 'tick' | 'ondemand' | 'manual';
}

// ── Evals (docs/TRAINING.md §6 — the agent-testing harness) ─────────────────

/** One scored episode of an eval run: the captured window's objective scores
 *  + constraint violations + forecast-quality block. `source`/`model` are set
 *  for eval `source:"model"` episodes (the user-defined scorer's own run —
 *  rendered by the eval dashboard's dedicated `model` tile). */
export interface EvalEpisode {
  episode: number;
  seed: number;
  ticks: number;
  steps: [number, number] | null;
  datasetRunId: string | null;
  source?: string;
  model?: string;
  scores: Record<string, number>;
  violations: { label: string; op: string; value: number; actual: number }[];
  forecasts?: Record<string, EvalForecastRecord>;
}

/** Forecast-quality record for one label: MAE/bias over the aligned
 *  predict-vs-actual pairs + (when a spike threshold is declared) the
 *  confusion block. Nulls = not computable (no pairs / empty denominators). */
export interface EvalForecastRecord {
  n: number;
  mae: number | null;
  bias: number | null;
  spike?: {
    tp: number; fp: number; fn: number;
    precision: number | null; recall: number | null;
  };
}

/** Anomaly-detection record for one detector: event-level confusion +
 *  detection latency (negative = the alarm fired before the incident). */
export interface EvalAnomalyRecord {
  n: number;
  tp: number;
  fp: number;
  fn: number;
  precision: number | null;
  recall: number | null;
  latency?: { mean: number | null; best: number | null; worst: number | null };
}

/** An eval report (live-polled or persisted under `<project>/evals/`). */
export interface EvalReport {
  evalId: string;
  projectId: string;
  config: Record<string, unknown>;
  status: 'running' | 'done' | 'failed' | 'cancelled';
  episodes: EvalEpisode[];
  aggregate: Record<string, number>;
  forecastAggregate?: Record<string, EvalForecastRecord>;
  anomalyAggregate?: Record<string, EvalAnomalyRecord>;
  error: string | null;
  startedAt: string;
  endedAt: string | null;
}

/** One durable dataset run (the registry row behind `GET /datasets`) — what
 *  the eval form's dataset picker lists. `meta` carries provenance (seed,
 *  eval id, episode, note) recorded at arm time. */
export interface DatasetRunInfo {
  runId: string;
  scenarioId?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  ticks?: number;
  provenance?: string;
  meta?: Record<string, unknown>;
}

/** POST /eval body — the knobs of an eval run. `agents: false` runs the
 *  BASELINE (agent runners swapped out for the eval's duration).
 * `source: 'dataset'` scores an existing durable dataset run offline;
 * `source: 'table'` scores one of the project's recorded tables (`snapshots`
 * telemetry, imported real data) through the objectives `column` sources —
 * both batch/replay paths need no model spawn; `source: 'model'` runs the
 * project's own `evaluate(ctx)` (models/<name>.py) and merges its metrics. */
export interface EvalStartOptions {
  episodes: number;
  ticks: number;
  seed?: number;
  agents: boolean;
  persist: boolean;
  note?: string;
  source?: 'live' | 'dataset' | 'table' | 'model';
  datasetRunId?: string;
  table?: string;
  model?: string;
}


/** A rendered plot's dispatch spec. The frontend lazy-loads react-plotly.js
 *  (`kind:'plotly'`), react-vega (`kind:'vega'`), or a hand-rolled table
 *  (`kind:'table'`) based on this field. `spec` is renderer-specific. */
export interface PlotSpec {
  kind: 'plotly' | 'vega' | 'table';
  spec: Record<string, unknown>;
  refresh: 'tick' | 'ondemand' | 'manual';
  title?: string;
}

/** One dashboard tab's document (a `dashboards/<id>.json` file). `plots` is the
 *  placed-plot layout for react-grid-layout. Persisted via the ordinary file
 *  API (PUT /files/dashboards/<id>.json). */
export interface DashboardDoc {
  id: string;
  name: string;
  plots: PlacedPlot[];
}

/** A plot placed on a dashboard canvas. Grid coordinates are react-grid-layout
 *  units (12-col grid, row height in px-ish). `refresh` overrides the plot's
 *  META default per-placement. `source` discriminates a code plot
 *  (`plots/<id>.py`, rendered via `/plots/render` in a subprocess) from a
 *  declarative chart (`charts/<id>.json`, built client-side by `buildChart`);
 *  absent → `'code'` for back-compat with pre-chart dashboards. */
export interface PlacedPlot {
  plotId: string;
  x: number;
  y: number;
  w: number;
  h: number;
  refresh?: 'tick' | 'ondemand' | 'manual';
  /** Discriminates a code plot (`plots/<id>.py`, rendered via /plots/render
   *  in a subprocess) from a declarative chart (`charts/<id>.json`, built
   *  client-side) from an eval-results tile (`<evalId>#<section>`, projected
   *  client-side from the eval report). Absent → `'code'` for back-compat. */
  source?: 'code' | 'chart' | 'eval';
}

export interface RemoteProjectAdapterOpts {
  /** Base URL for the projects API, e.g. `http://localhost:8000/api/projects`. */
  baseUrl: string;
  /** Optional bearer/api-key. */
  apiKey?: string;
  /** Fetch override (testing). */
  fetchImpl?: typeof fetch;
}

// ── Git import (backend/projects/git_import.py) ─────────────────────────────

/** A stored git credential's kind. `ssh_key` = a pasted private key (used via
 *  GIT_SSH_COMMAND); `password` = classic HTTPS basic auth; `pat` = a personal
 *  access token over HTTPS (for Forgejo, the same token also authenticates the
 *  API archive endpoint). */
export type GitCredentialKind = 'ssh_key' | 'password' | 'pat';

/** One stored per-host credential — metadata only; the secret never comes
 *  back from the API. From `GET /api/git/credentials`. */
export interface GitCredentialInfo {
  host: string;
  kind: GitCredentialKind;
  username: string | null;
  updated_at: number;
}

/** What `POST /api/git/lookup` reports for a pasted remote URL: the parsed
 *  host/repo, the normalized ssh/https URL forms, and whether a credential
 *  is stored for the host (kind + username only). */
export interface GitLookup {
  scheme: 'https' | 'http' | 'ssh';
  host: string;
  repo_name: string;
  https_url: string;
  ssh_url: string;
  credential: { kind: GitCredentialKind; username: string | null } | null;
}

/** An inline (one-shot) credential for a single import — not persisted
 *  unless the caller also passes `saveCredential`. */
export interface GitCredentialInput {
  kind: GitCredentialKind;
  username?: string;
  secret: string;
}

export class RemoteProjectAdapter implements LibraryAdapter {
  private readonly baseUrl: string;
  private readonly apiKey?: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: RemoteProjectAdapterOpts) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.apiKey = opts.apiKey;
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  // ── LibraryAdapter ──────────────────────────────────────────────────────

  async list(): Promise<ProjectEntry[]> {
    const res = await this.request('GET', '');
    const body = (await res.json()) as { entries: ProjectEntry[] };
    return body.entries;
  }

  async search(query: string): Promise<ProjectEntry[]> {
    const q = query.trim();
    const path = q ? `?q=${encodeURIComponent(q)}` : '';
    const res = await this.request('GET', path);
    const body = (await res.json()) as { entries: ProjectEntry[] };
    return body.entries;
  }

  async get(id: string): Promise<ScenarioBundle | null> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/graph`, undefined, true);
    if (res.status === 404) return null;
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as ScenarioBundle;
  }

  async save(bundle: ScenarioBundle, opts?: { slug?: string; tags?: string[] }): Promise<ProjectEntry> {
    // Projects are uploaded as ZIPs; "save" from the editor is a bundle-only
    // update. For now, this delegates to importBundle (create a new project
    // from the bundle). A full project save (graph + manifest) is a future
    // enhancement once the editor can edit the manifest.
    return this.importBundle(bundle, opts);
  }

  async rename(id: string, label: string): Promise<ProjectEntry> {
    const res = await this.request('PATCH', `/${encodeURIComponent(id)}`, { label });
    const body = (await res.json()) as { entry: ProjectEntry };
    return body.entry;
  }

  async setTags(id: string, tags: string[]): Promise<ProjectEntry> {
    const res = await this.request('PATCH', `/${encodeURIComponent(id)}`, { tags });
    const body = (await res.json()) as { entry: ProjectEntry };
    return body.entry;
  }

  async delete(id: string): Promise<void> {
    await this.request('DELETE', `/${encodeURIComponent(id)}`);
  }

  async importBundle(bundle: ScenarioBundle, opts?: { slug?: string; tags?: string[] }): Promise<ProjectEntry> {
    // Build a minimal project ZIP from the bundle + upload it. Slugify the
    // requested slug (callers pass a human label like "Food Delivery copy") so
    // the upload's project_id query param is URL/filesystem-safe AND matches
    // the manifest id inside the ZIP (which buildProjectZip also slugifies).
    const slug = opts?.slug ? slugify(opts.slug) : undefined;
    const zip = await buildProjectZip(bundle, { ...opts, slug });
    const res = await this.uploadZip(zip, slug);
    const body = (await res.json()) as { entry: ProjectEntry };
    return body.entry;
  }

  async createBlank(
    label: string,
    opts?: { description?: string; tags?: string[] },
  ): Promise<ProjectEntry> {
    // The server-side scaffold: it mints the id from the slugified label
    // (suffixed on collision — no uuids) and writes the full starter layout
    // (manifest with every surface enabled, empty Main graph, tables spec,
    // docker-compose.yml). Via POST /api/projects/create.
    const res = await this.request('POST', '/create', {
      label,
      description: opts?.description,
      tags: opts?.tags ?? [],
    });
    const body = (await res.json()) as { entry: ProjectEntry };
    return body.entry;
  }

  // ── Project-specific (beyond LibraryAdapter) ────────────────────────────

  /** Download the project as a ZIP archive. Returns the blob (for save-as). */
  async download(id: string): Promise<Blob> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/download`);
    return await res.blob();
  }

  /** List the project's runnable source files under `agents.codeRoot`. Used by
   *  the agent modal's Code-tab file picker — `module` is the dotted path that
   *  becomes `AgentConfig.target`. Returns `[]` when the project has no `src/`. */
  async listSrcFiles(id: string): Promise<SourceFile[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/files`);
    const body = (await res.json()) as { files: SourceFile[] };
    return body.files;
  }

  /** List EVERY text file + directory in the project (`src/*.py`, `data/*.sql`,
   *  `project.json`, `requirements.txt`, …). Used by the Code tab's file-tree
   *  sidebar. Returns `[]` for a graph-only project (no `src/`). The path
   *  carries `/` separators; `kind` selects the Monaco language + tree icon.
   *  From `GET /files?all=1`. */
  async listAllFiles(id: string): Promise<ProjectFile[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/files?all=1`);
    const body = (await res.json()) as { files: ProjectFile[] };
    return body.files;
  }

  /** Read one project file's UTF-8 contents. `path` is POSIX-style relative to
   *  the project root (e.g. `src/economy.py`). From `GET /files/{path}`.
   *  The backend sandboxes the path (rejects `..`, absolute, escapes). */
  async readFile(id: string, path: string): Promise<string> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/files/${encodeFilePath(path)}`);
    const body = (await res.json()) as { path: string; content: string };
    return body.content;
  }

  /** Write one project file's contents (explicit save — Ctrl+S / Save button).
   *  `path` is POSIX-style; the backend enforces a writable-suffix allowlist
   *  (`.py/.json/.sql/.txt/.md/.csv/.toml/.cfg/.ini`). A running model
   *  subprocess picks up code changes on its next `import`. Via
   *  `PUT /files/{path}`. */
  async writeFile(id: string, path: string, content: string): Promise<void> {
    await this.request('PUT', `/${encodeURIComponent(id)}/files/${encodeFilePath(path)}`, { content });
  }

  /** Create a file or directory. `opts.dir=true` makes a directory; otherwise an
   *  empty file (or `opts.content` if given). Refuses to overwrite (409) and
   *  enforces the writable-suffix allowlist for files. */
  async createFile(id: string, path: string, opts?: { dir?: boolean; content?: string }): Promise<void> {
    await this.request('POST', `/${encodeURIComponent(id)}/files/${encodeFilePath(path)}`, opts ?? {});
  }

  /** Rename/move a file or directory. The target must not exist (409 otherwise);
   *  file targets must pass the writable-suffix allowlist. */
  async moveFile(id: string, from: string, to: string): Promise<void> {
    await this.request('PATCH', `/${encodeURIComponent(id)}/files/${encodeFilePath(from)}`, { to });
  }

  /** Delete a file or directory (recursive for dirs). */
  async deleteFile(id: string, path: string): Promise<void> {
    await this.request('DELETE', `/${encodeURIComponent(id)}/files/${encodeFilePath(path)}`);
  }

  /** Write the project's starter AGENTS.md — the instructions file coding
   *  agents (external harnesses + the Lab chat) read. Throws (409) when one
   *  already exists, so a hand-customised file is never clobbered. Via
   *  `POST /agent-instructions`. */
  async initAgentInstructions(id: string): Promise<void> {
    await this.request('POST', `/${encodeURIComponent(id)}/agent-instructions`);
  }

  /** Poll the unified task list — the spawned model subprocess + the Jupyter
   *  notebook server (when the manifest enables it) — plus one entry per live
   *  kernel (joined with its notebook file name). Used by the Code tab's Tasks
   *  panel. Read-only; start/stop stay on `runStart/runStop` /
   *  `notebook/notebookStop`, and kernel interrupt on `interruptKernel`. */
  async tasks(id: string): Promise<{ tasks: ProjectTask[]; kernels: KernelInfo[] }> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/tasks`);
    const body = (await res.json()) as { tasks: ProjectTask[]; kernels: KernelInfo[] };
    return { tasks: body.tasks ?? [], kernels: body.kernels ?? [] };
  }

  /** Interrupt a single kernel by id (halts one computation without stopping
   *  the notebook server or other kernels). Returns true on success; false
   *  (not a throw) when the server is down or the id is unknown — the Tasks
   *  panel treats that as transient poll-stale. From
   *  `POST /notebook/kernels/{id}/interrupt`. */
  async interruptKernel(id: string, kernelId: string): Promise<boolean> {
    const res = await this.request(
      'POST',
      `/${encodeURIComponent(id)}/notebook/kernels/${encodeURIComponent(kernelId)}/interrupt`,
    );
    const body = (await res.json()) as { ok: boolean };
    return !!body.ok;
  }

  // ─── Plot tab ────────────────────────────────────────────────────────────
  // The plot registry + isolated render runtime (see backend/plots/). These
  // are project-specific surfaces beyond the LibraryAdapter contract — same
  // pattern as download/runStart. Dashboard docs are plain files under
  // dashboards/*.json, read/written via readFile/writeFile above.

  /** List the project's registered plots (the Plot tab's left rail). Backend
   *  AST-parses each `plots/*.py`'s `META` dict — never imports, so listing
   *  is cheap and side-effect-free. From `GET /plots`. */
  async listPlots(id: string): Promise<PlotMeta[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/plots`);
    const body = (await res.json()) as { plots: PlotMeta[] };
    return body.plots;
  }

  /** Render a batch of plots in one isolated subprocess. The frontend batches
   *  every dashboard tile's plots into one call so the worker spawns once per
   *  refresh tick. Returns per-plot specs + per-plot errors (a plot that raised
   *  / timed out appears in errors, not specs). From `POST /plots/render`. */
  async renderPlots(
    id: string,
    plotIds: string[],
    ctx: Record<string, unknown>,
  ): Promise<{ specs: Record<string, PlotSpec>; errors: Record<string, string> }> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/plots/render`, {
      plots: plotIds,
      ctx,
    });
    const body = (await res.json()) as { specs: Record<string, PlotSpec>; errors: Record<string, string> };
    return { specs: body.specs ?? {}, errors: body.errors ?? {} };
  }

  /** List the project's dashboard documents (the dashboard tabs). Lightweight
   *  index — reads each `dashboards/*.json`'s id+name only; the full doc is
   *  fetched via `readFile(id, 'dashboards/<id>.json')`. From `GET /dashboards`. */
  async listDashboards(id: string): Promise<{ id: string; name: string }[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/dashboards`);
    const body = (await res.json()) as { dashboards: { id: string; name: string }[] };
    return body.dashboards ?? [];
  }

  /** List the project's declarative (no-code) charts — the ChartSpec files under
   *  `charts/*.json`. Lightweight index (id/label/chartType/refresh) returned by
   *  `GET /charts`; the full spec is fetched on demand via `readChart`. Charts
   *  render client-side via `buildChart` (no `/plots/render` subprocess). */
  async listCharts(id: string): Promise<ChartEntry[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/charts`);
    const body = (await res.json()) as { charts: ChartEntry[] };
    return body.charts ?? [];
  }

  /** Read one declarative chart's full ChartSpec. Stored at
   *  `charts/<chartId>.json`; the backend treats it as an opaque project file. */
  async readChart(id: string, chartId: string): Promise<ChartSpec> {
    const raw = await this.readFile(id, `charts/${chartId}.json`);
    return JSON.parse(raw) as ChartSpec;
  }

  /** Persist a declarative chart (create or update). Writes `charts/<id>.json`
   *  through the generic file API (`.json` is on the writable allowlist). */
  async saveChart(id: string, spec: ChartSpec): Promise<void> {
    await this.writeFile(id, `charts/${spec.id}.json`, JSON.stringify(spec, null, 2));
  }

  /** Delete a declarative chart's spec file. */
  async deleteChart(id: string, chartId: string): Promise<void> {
    await this.deleteFile(id, `charts/${chartId}.json`);
  }

  /** Run a SQL query against the project's tables DB — the same seam the Data
   *  tab's SQL console uses (collections on the platform, one auth
   *  path). Returns the result rows as objects keyed by column. Used by
   *  SQL-datasource charts; the query does the aggregation, the chart
   *  constructor does the visuals. */
  async runSql(
    id: string,
    query: string,
    limit = 10000,
  ): Promise<{ columns: string[]; rows: Record<string, unknown>[] }> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/sql/execute`, { query });
    const body = (await res.json()) as { type: string; query_id?: string; columns?: string[] };
    if (body.type !== 'table' || !body.query_id) {
      throw new Error('Query returned no result set (mutation or empty response).');
    }
    const rres = await this.request(
      'GET',
      `/${encodeURIComponent(id)}/sql/results/${body.query_id}?limit=${limit}`,
    );
    const rbody = (await rres.json()) as { rows: Record<string, unknown>[] };
    return { columns: body.columns ?? [], rows: rbody.rows ?? [] };
  }

  /** The project's declared platform view presets (manifest `tables.views`)
   *  with their columns — the historical reporting surface charts read. */
  async listViews(
    id: string,
  ): Promise<
    { table: string; name: string; filter?: string; sort?: string; columns: { name: string; type: string }[] }[]
  > {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/views`);
    const body = (await res.json()) as {
      views: {
        table: string;
        name: string;
        filter?: string;
        sort?: string;
        columns: { name: string; type: string }[];
      }[];
    };
    return body.views ?? [];
  }

  /** Read one view preset's rows through the records view endpoint — the
   *  `filter` is the VSKI filter language (ANDs with the preset's) and `sort`
   *  is `col`/`-col` (replaces the preset's). */
  async readView(
    id: string,
    table: string,
    view: string,
    opts?: { filter?: string; sort?: string; limit?: number },
  ): Promise<{ columns: string[]; rows: Record<string, unknown>[] }> {
    const params = new URLSearchParams();
    if (opts?.filter) params.set('filter', opts.filter);
    if (opts?.sort) params.set('sort', opts.sort);
    params.set('limit', String(opts?.limit ?? 10000));
    const res = await this.request(
      'GET',
      `/${encodeURIComponent(id)}/views/${encodeURIComponent(table)}/${encodeURIComponent(view)}?${params.toString()}`,
    );
    const body = (await res.json()) as { columns?: string[]; rows?: Record<string, unknown>[] };
    return { columns: body.columns ?? [], rows: body.rows ?? [] };
  }

  /** Duplicate a project as a FULL package copy: download the source's ZIP
   *  (graph + manifest + src/ + data/ — everything), then re-upload it under
   *  a fresh id. This preserves the model, tables schema/seed, and code — a
   *  graph-only `importBundle` would drop all of that, leaving a copy that
   *  can't run and has no tables. The new id is the slugified LABEL (no
   *  uuids); a collision retries once with a `-2` suffix before surfacing
   *  the backend's 409. */
  async duplicate(sourceId: string, opts?: { id?: string; label?: string; tags?: string[] }): Promise<ProjectEntry> {
    const base = opts?.id ?? slugify(opts?.label ?? `${sourceId}-copy`);
    const zip = await this.download(sourceId);
    let res = await this.uploadZip(zip, base);
    if (res.status === 409) {
      res = await this.uploadZip(zip, `${base}-2`);
    }
    if (!res.ok) throw await this.toError(res);
    const body = (await res.json()) as { entry: ProjectEntry };
    const entry = body.entry;
    // Apply the new label/tags if given (the upload kept the source's label).
    if (opts?.label && opts.label !== entry.label) {
      return await this.rename(entry.id, opts.label);
    }
    if (opts?.tags) {
      return await this.setTags(entry.id, opts.tags);
    }
    return entry;
  }

  /** Upload a pre-built project ZIP. Returns the server's response. */
  async uploadZip(zip: Blob | ArrayBuffer, projectId?: string): Promise<Response> {
    const path = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    const headers: Record<string, string> = {};
    if (this.apiKey) headers.Authorization = `Bearer ${this.apiKey}`;
    const form = new FormData();
    form.append('file', new Blob([zip]), `${projectId ?? 'project'}.zip`);
    return this.fetchImpl(`${this.baseUrl}${path}`, { method: 'POST', headers, body: form });
  }

  /** Import a project from a ZIP archive (the full package format: graph +
   *  manifest + src/ + data/). Mints a fresh UUID project id, uploads, applies
   *  the user's label, and returns the resulting entry. This is the upload-tab
   *  surface — replaces the old JSON-only importBundle path (a project can have
   *  multiple graphs + tables + model code, none of which survive a JSON-only
   *  export). */
  async importZip(zip: Blob | ArrayBuffer, opts?: { label?: string }): Promise<ProjectEntry> {
    const newId = uuid();
    const res = await this.uploadZip(zip, newId);
    if (!res.ok) throw await this.toError(res);
    const body = (await res.json()) as { entry: ProjectEntry };
    const entry = body.entry;
    if (opts?.label && opts.label !== entry.label) {
      return await this.rename(entry.id, opts.label);
    }
    return entry;
  }

  /** Import a project from a git remote (a shallow clone — one branch, no
   *  history). The repo's own project.json is respected; without one the
   *  backend mints a manifest from the repo name. `credential` is a one-shot
   *  credential (the transport follows its kind: an ssh_key forces ssh even
   *  for a pasted https URL and vice versa); `saveCredential` additionally
   *  persists it for the host in the encrypted keystore. Via
   *  `POST /api/projects/import/git`. */
  async importGit(
    url: string,
    opts?: { label?: string; ref?: string; credential?: GitCredentialInput; saveCredential?: boolean },
  ): Promise<ProjectEntry> {
    const res = await this.request(
      'POST',
      '/import/git',
      {
        url,
        label: opts?.label,
        ref: opts?.ref,
        credential: opts?.credential ?? null,
        save_credential: opts?.saveCredential ?? false,
      },
      true,
    );
    if (!res.ok) throw await this.toError(res);
    const body = (await res.json()) as { entry: ProjectEntry };
    return body.entry;
  }

  /** Import a project from a Forgejo instance (vski.sh) via the API's archive
   *  download — a one-shot zip snapshot, no git history. Public repos import
   *  anonymously; `token` (a PAT) unlocks private ones. Via
   *  `POST /api/projects/import/forgejo`. */
  async importForgejo(
    url: string,
    opts?: { label?: string; ref?: string; token?: string; saveToken?: boolean; username?: string },
  ): Promise<ProjectEntry> {
    const res = await this.request(
      'POST',
      '/import/forgejo',
      {
        url,
        label: opts?.label,
        ref: opts?.ref,
        token: opts?.token ?? null,
        save_token: opts?.saveToken ?? false,
        username: opts?.username ?? null,
      },
      true,
    );
    if (!res.ok) throw await this.toError(res);
    const body = (await res.json()) as { entry: ProjectEntry };
    return body.entry;
  }

  // ── Git credential management (/api/git/*) ─────────────────────────────

  /** Parse a pasted remote URL and report what an import would use: the
   *  host, the repo name, and whether a credential is stored for the host.
   *  The Import tab calls this as the user types. */
  async gitLookup(url: string): Promise<GitLookup> {
    const res = await this.gitApi('POST', '/git/lookup', { url });
    return (await res.json()) as GitLookup;
  }

  /** Verify remote access (`git ls-remote`) with an inline credential, the
   *  stored one, or none — the credential form's Test button. Returns
   *  ok=false + git's message instead of throwing on auth/not-found. */
  async gitTest(url: string, credential?: GitCredentialInput): Promise<{ ok: boolean; detail?: string }> {
    const res = await this.gitApi('POST', '/git/test', { url, credential: credential ?? null });
    return (await res.json()) as { ok: boolean; detail?: string };
  }

  /** The stored per-host git credentials (metadata only — never secrets).
   *  `enabled` is false when the keystore can't persist (the save checkbox
   *  is hidden then). */
  async listGitCredentials(): Promise<{ enabled: boolean; credentials: GitCredentialInfo[] }> {
    const res = await this.gitApi('GET', '/git/credentials');
    return (await res.json()) as { enabled: boolean; credentials: GitCredentialInfo[] };
  }

  /** Store (replace) the credential for a host — one credential per host:
   *  domains are the sharing unit. */
  async storeGitCredential(
    host: string,
    kind: GitCredentialKind,
    secret: string,
    username?: string,
  ): Promise<void> {
    await this.gitApi('PUT', '/git/credentials', { host, kind, username: username ?? null, secret });
  }

  /** Remove the stored credential for a host. */
  async deleteGitCredential(host: string): Promise<void> {
    await this.gitApi('DELETE', `/git/credentials/${encodeURIComponent(host)}`);
  }

  /** Start the project's model subprocess. */
  async runStart(id: string): Promise<ProjectRunStatus> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/run/start`);
    return (await res.json()) as ProjectRunStatus;
  }

  /** Stop the project's model subprocess. */
  async runStop(id: string): Promise<ProjectRunStatus> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/run/stop`);
    return (await res.json()) as ProjectRunStatus;
  }

  /** Read the run status. */
  async runStatus(id: string): Promise<ProjectRunStatus> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/run`);
    return (await res.json()) as ProjectRunStatus;
  }

  /** Toggle the run-level agents kill-switch. When disabled the relay path
   *  skips agent application entirely — ticks pass through unmodified. */
  async setAgentsEnabled(id: string, enabled: boolean): Promise<void> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/agents/toggle`, { enabled },
    );
    if (!res.ok) throw await this.toError(res);
  }

  /** Get or start the notebook server; returns the proxied URL to open. */
  async notebook(id: string): Promise<NotebookStatus> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/notebook`);
    return (await res.json()) as NotebookStatus;
  }

  /** Stop the notebook server. */
  async notebookStop(id: string): Promise<NotebookStatus> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/notebook/stop`);
    return (await res.json()) as NotebookStatus;
  }

  /** Spawn one of the project's trainers (docs/TRAINING.md §5). Results land
   *  in the artifact registry (`listArtifacts`), not the process status. */
  async trainerStart(
    id: string,
    trainer: string,
    opts?: { datasets?: string[]; seed?: number },
  ): Promise<Record<string, unknown>> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/trainers/${encodeURIComponent(trainer)}/start`,
      { datasets: opts?.datasets ?? [], seed: opts?.seed },
    );
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as Record<string, unknown>;
  }

  /** Terminate a trainer subprocess (SIGTERM → SIGKILL). */
  async trainerStop(id: string, trainer: string): Promise<Record<string, unknown>> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/trainers/${encodeURIComponent(trainer)}/stop`);
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as Record<string, unknown>;
  }

  /** The artifact registry index (name → versions + latest). */
  async listArtifacts(id: string): Promise<{ artifacts: Record<string, unknown> }> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/artifacts`);
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as { artifacts: Record<string, unknown> };
  }

  /** The project's models (AST-parsed META — docs/TRAINING.md §7). Drives the
   *  Tasks panel's model rows and the eval form's model picker. */
  async listModels(id: string): Promise<ProjectModelInfo[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/models`);
    if (!res.ok) throw await this.toError(res);
    const data = (await res.json()) as { models?: ProjectModelInfo[] };
    return data.models ?? [];
  }

  /** The project's workflow names (module stems under workflows/) — the
   *  NodeModal trigger editor's target picker. Thin: only ids/names. */
  async listWorkflows(id: string): Promise<{ id: string; name: string }[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/workflows`);
    if (!res.ok) throw await this.toError(res);
    const data = (await res.json()) as { workflows?: { id?: string; name: string }[] };
    return (data.workflows ?? []).map((w) => ({ id: w.id ?? w.name, name: w.name }));
  }

  /** Stop the project's workflow worker task (the `workflows` Tasks row;
   *  start is the Workflows rail's own control). */
  async workflowWorkerStop(id: string): Promise<Record<string, unknown>> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/workflows/worker/stop`,
    );
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as Record<string, unknown>;
  }

  /** The project's workflows settings (worker autostart) — the Settings →
   *  Engine toggle's hydration source. */
  async getWorkflowSettings(id: string): Promise<{ enabled: boolean; autostart: boolean }> {
    const res = await this.request(
      'GET', `/${encodeURIComponent(id)}/workflows/settings`,
    );
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as { enabled: boolean; autostart: boolean };
  }

  /** Persist the worker autostart preference to the project manifest. */
  async setWorkflowAutostart(id: string, autostart: boolean): Promise<void> {
    const res = await this.request(
      'PUT', `/${encodeURIComponent(id)}/workflows/settings`, { autostart },
    );
    if (!res.ok) throw await this.toError(res);
  }

  /** Run one of the project's models (`entry`: 'score' batch inference,
   *  'train' like a trainer, 'evaluate' the user-defined scorer). Inputs
   *  default to the model's declared META inputs. Poll `modelStatus`. */
  async modelRun(
    id: string,
    model: string,
    opts?: { entry?: string; datasets?: string[]; params?: Record<string, unknown>; seed?: number },
  ): Promise<Record<string, unknown>> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/models/${encodeURIComponent(model)}/run`,
      { entry: opts?.entry ?? 'score', datasets: opts?.datasets, params: opts?.params, seed: opts?.seed },
    );
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as Record<string, unknown>;
  }

  /** Terminate a model-run subprocess (SIGTERM → SIGKILL). */
  async modelRunStop(id: string, model: string): Promise<Record<string, unknown>> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/models/${encodeURIComponent(model)}/stop`);
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as Record<string, unknown>;
  }

  /** One model's run status (running/pid/exit_code + the worker's result). */
  async modelStatus(id: string, model: string): Promise<Record<string, unknown>> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/models/${encodeURIComponent(model)}/status`);
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as Record<string, unknown>;
  }

  /** List eval reports (live + persisted, newest last — docs/TRAINING.md §6). */
  async listEvals(id: string): Promise<{ evals: EvalReport[] }> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/evals`);
    return (await res.json()) as { evals: EvalReport[] };
  }

  /** The project's tables (name + row count) — the eval form's table picker
   *  and anywhere a table name must be CHOSEN, not typed. */
  async listTables(id: string): Promise<{ name: string; count: number }[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/tables`);
    if (!res.ok) throw await this.toError(res);
    const data = (await res.json()) as { tables?: { name?: unknown; count?: unknown }[] };
    return (data.tables ?? [])
      .filter((t) => typeof t?.name === 'string')
      // Internal chat collections are plumbing, not project data.
      .filter((t) => t.name !== 'chat_threads' && t.name !== 'chat_messages')
      .map((t) => ({ name: t.name as string, count: Number(t.count ?? 0) }));
  }

  /** The project's durable dataset runs (newest last) — the eval form's run
   *  picker. Finally VISIBLE in the UI: runId, tick count, provenance, dates. */
  async listDatasets(id: string): Promise<DatasetRunInfo[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/datasets`);
    if (!res.ok) throw await this.toError(res);
    const data = (await res.json()) as { datasets?: DatasetRunInfo[] };
    return data.datasets ?? [];
  }

  /** One dataset run's bounds + registry meta — the replay surface's index
   *  probe (never reads tick payloads). */
  async getDatasetWindow(id: string, runId: string): Promise<ReplayRunInfo> {
    const res = await this.request(
      'GET', `/${encodeURIComponent(id)}/datasets/${encodeURIComponent(runId)}/window`,
    );
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as ReplayRunInfo;
  }

  /** A step-window slice of a dataset run's recorded ticks — the replay
   *  surface's paged read (inclusive bounds, capped by the route). */
  async getDatasetTicks(
    id: string,
    runId: string,
    opts: { from: number; to: number; limit: number },
  ): Promise<ReplayTickRow[]> {
    const q = new URLSearchParams({
      from: String(opts.from),
      to: String(opts.to),
      limit: String(opts.limit),
    });
    const res = await this.request(
      'GET',
      `/${encodeURIComponent(id)}/datasets/${encodeURIComponent(runId)}/ticks?${q}`,
    );
    if (!res.ok) throw await this.toError(res);
    const data = (await res.json()) as { rows?: ReplayTickRow[] };
    return data.rows ?? [];
  }

  /** The project's saved replay periods (reference docs) — `GET /replays`. */
  async listReplays(id: string): Promise<ReplayIndexEntry[]> {
    const res = await this.request('GET', `/${encodeURIComponent(id)}/replays`);
    if (!res.ok) throw await this.toError(res);
    const data = (await res.json()) as { replays?: ReplayIndexEntry[] };
    return data.replays ?? [];
  }

  /** Rename a dataset run (its display name — registry `meta.label`, what
   *  the picker + a run's replay tab show). The rename half of a run-derived
   *  replay tab's double-click. */
  async renameDatasetRun(id: string, runId: string, label: string): Promise<void> {
    const res = await this.request(
      'POST',
      `/${encodeURIComponent(id)}/datasets/${encodeURIComponent(runId)}/label`,
      { label },
    );
    if (!res.ok) throw await this.toError(res);
  }

  /** One saved replay doc (`replays/<docId>.json` — the ordinary file API). */
  async readReplay(id: string, docId: string): Promise<ReplayDoc> {
    const raw = await this.readFile(id, `replays/${docId}.json`);
    return JSON.parse(raw) as ReplayDoc;
  }

  /** One eval report by id (running or persisted). */
  async getEval(id: string, evalId: string): Promise<EvalReport> {
    const res = await this.request(
      'GET', `/${encodeURIComponent(id)}/evals/${encodeURIComponent(evalId)}`,
    );
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as EvalReport;
  }

  /** Start an eval run (a background task — poll `listEvals`). `agents:false`
   *  runs the baseline (runner agents swapped out for the eval's duration). */
  async evalStart(id: string, opts: EvalStartOptions): Promise<EvalReport> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/eval`, opts, true,
    );
    if (!res.ok) throw await this.toError(res);
    return (await res.json()) as EvalReport;
  }

  /** Flag a running eval cancelled (finishes the current episode's cleanup). */
  async evalCancel(id: string, evalId: string): Promise<void> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/evals/${encodeURIComponent(evalId)}/cancel`, undefined, true,
    );
    if (!res.ok) throw await this.toError(res);
  }

  /** Install project dependencies (runs the manifest's install.command). */
  async install(id: string): Promise<string> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/install`);
    return await res.text();
  }

  /** Start the project's image refresh — `docker compose pull` + `build`
   *  against its compose file, a BACKGROUND task (poll the Tasks panel's
   *  Images row / the status route). Docker-execution deployments only. */
  async imagesRebuild(id: string): Promise<void> {
    const res = await this.request('POST', `/${encodeURIComponent(id)}/images/rebuild`, undefined, true);
    if (!res.ok) throw await this.toError(res);
  }

  /** One manifest-declared custom task's status + tailed output — the Tasks
   *  panel row's log view polls this while open. */
  async taskStatus(id: string, name: string): Promise<CustomTaskStatus> {
    const res = await this.request(
      'GET', `/${encodeURIComponent(id)}/tasks/${encodeURIComponent(name)}`,
    );
    return await res.json();
  }

  /** Start one custom task (the row's Run button): a one-shot run of the
   *  task's command. 409s when already running — surfaced by the caller. */
  async taskStart(id: string, name: string): Promise<CustomTaskStatus> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/tasks/${encodeURIComponent(name)}/start`, {},
    );
    return await res.json();
  }

  /** Stop one custom task (the row's Stop button). */
  async taskStop(id: string, name: string): Promise<CustomTaskStatus> {
    const res = await this.request(
      'POST', `/${encodeURIComponent(id)}/tasks/${encodeURIComponent(name)}/stop`, {},
    );
    return await res.json();
  }

  // ── Internal ────────────────────────────────────────────────────────────

  private async request(
    method: string,
    path: string,
    body?: unknown,
    rawStatus = false,
  ): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.apiKey) headers.Authorization = `Bearer ${this.apiKey}`;
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (e) {
      throw new LibraryError('network', e instanceof Error ? e.message : 'Network request failed.');
    }
    if (rawStatus) return res;
    if (!res.ok) throw await this.toError(res);
    return res;
  }

  private async toError(res: Response): Promise<LibraryError> {
    let detail = '';
    try {
      const body = (await res.json()) as { detail?: unknown };
      detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail ?? body);
    } catch {
      detail = res.statusText;
    }
    const code: LibraryError['code'] =
      res.status === 404 ? 'not-found'
      : res.status === 409 ? 'collision'
      : res.status === 400 ? 'invalid'
      : 'network';
    return new LibraryError(code, detail || `${res.status} ${res.statusText}`);
  }

  /** Request against the API root (`…/api`) instead of the projects base —
   *  the git-credential routes live at `/api/git/*`, outside this adapter's
   *  `/api/projects` prefix. Same auth + error mapping as `request`. */
  private async gitApi(method: string, path: string, body?: unknown): Promise<Response> {
    const root = this.baseUrl.replace(/\/projects\/?$/, '') || this.baseUrl;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.apiKey) headers.Authorization = `Bearer ${this.apiKey}`;
    let res: Response;
    try {
      res = await this.fetchImpl(`${root}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (e) {
      throw new LibraryError('network', e instanceof Error ? e.message : 'Network request failed.');
    }
    if (!res.ok) throw await this.toError(res);
    return res;
  }
}

// ── ZIP builder (browser) ──────────────────────────────────────────────────

/** Build a minimal project ZIP from a ScenarioBundle in the browser, using
 *  a simple ZIP writer (no compression — STORE method). This is used by
 *  `importBundle` / `save` to upload a graph-only bundle as a project.
 *
 *  A full project ZIP (with code files + manifest run config) is built
 *  server-side when the user downloads an existing project. */
async function buildProjectZip(
  bundle: ScenarioBundle,
  opts?: { slug?: string; tags?: string[] },
): Promise<ArrayBuffer> {
  const pid = opts?.slug ?? slugify(bundle.label);
  const manifest = {
    schema: 1,
    id: pid,
    label: bundle.label,
    description: bundle.description ?? '',
    tags: opts?.tags ?? [],
  };
  const graphJson = JSON.stringify(bundle);
  const manifestJson = JSON.stringify(manifest, null, 2);
  // Minimal ZIP (STORE, no compression) — two entries.
  return buildMinimalZip({
    'project.json': manifestJson,
    'graph.json': graphJson,
  });
}

function slugify(label: string): string {
  let s = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  if (!s) s = 'untitled';
  return s.slice(0, 64);
}

/** Encode a POSIX-style file path for use in a URL path segment. Each segment
 *  is `encodeURIComponent`'d so `/` is preserved as the path separator while
 *  spaces + special chars in filenames are escaped. e.g. `src/my file.py`
 *  → `src/my%20file.py`. The backend's `{path:path}` route captures the rest. */
function encodeFilePath(path: string): string {
  return path
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

/** A minimal ZIP writer using the STORE (no-compression) method. Sufficient
 *  for small JSON payloads; the server re-validates + can re-pack. */
function buildMinimalZip(files: Record<string, string>): ArrayBuffer {
  // We need CRC32 for each entry. This is a synchronous implementation.
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const [name, content] of Object.entries(files)) {
    const nameBytes = encoder.encode(name);
    const data = encoder.encode(content);
    const crc = crc32(data);
    // Local file header (30 bytes + name).
    const local = new ArrayBuffer(30 + nameBytes.length);
    const dv = new DataView(local);
    dv.setUint32(0, 0x04034b50, true); // signature
    dv.setUint16(4, 20, true); // version needed
    dv.setUint16(6, 0, true); // flags
    dv.setUint16(8, 0, true); // compression (0 = stored)
    dv.setUint16(10, 0, true); // mod time
    dv.setUint16(12, 0x21, true); // mod date (1980-01-01)
    dv.setUint32(14, crc, true); // crc32
    dv.setUint32(18, data.length, true); // compressed size
    dv.setUint32(22, data.length, true); // uncompressed size
    dv.setUint16(26, nameBytes.length, true); // filename length
    dv.setUint16(28, 0, true); // extra field length
    new Uint8Array(local).set(nameBytes, 30);

    chunks.push(new Uint8Array(local));
    chunks.push(data);

    // Central directory record.
    const cd = new ArrayBuffer(46 + nameBytes.length);
    const cdv = new DataView(cd);
    cdv.setUint32(0, 0x02014b50, true); // signature
    cdv.setUint16(4, 20, true); // version made by
    cdv.setUint16(6, 20, true); // version needed
    cdv.setUint16(8, 0, true); // flags
    cdv.setUint16(10, 0, true); // compression
    cdv.setUint16(12, 0, true); // mod time
    cdv.setUint16(14, 0x21, true); // mod date
    cdv.setUint32(16, crc, true);
    cdv.setUint32(20, data.length, true);
    cdv.setUint32(24, data.length, true);
    cdv.setUint16(28, nameBytes.length, true);
    cdv.setUint16(30, 0, true); // extra
    cdv.setUint16(32, 0, true); // comment
    cdv.setUint16(34, 0, true); // disk number
    cdv.setUint16(36, 0, true); // internal attrs
    cdv.setUint32(38, 0, true); // external attrs
    cdv.setUint32(42, offset, true); // offset of local header
    new Uint8Array(cd).set(nameBytes, 46);
    central.push(new Uint8Array(cd));

    offset += local.byteLength + data.length;
  }

  const cdSize = central.reduce((s, c) => s + c.length, 0);
  const eod = new ArrayBuffer(22);
  const eodv = new DataView(eod);
  eodv.setUint32(0, 0x06054b50, true);
  eodv.setUint16(4, 0, true);
  eodv.setUint16(6, 0, true);
  eodv.setUint16(8, Object.keys(files).length, true);
  eodv.setUint16(10, Object.keys(files).length, true);
  eodv.setUint32(12, cdSize, true);
  eodv.setUint32(16, offset, true);
  eodv.setUint16(20, 0, true);

  const total = offset + cdSize + 22;
  const out = new Uint8Array(total);
  let pos = 0;
  for (const c of chunks) { out.set(c, pos); pos += c.length; }
  for (const c of central) { out.set(c, pos); pos += c.length; }
  out.set(new Uint8Array(eod), pos);
  return out.buffer;
}

/** CRC32 lookup table + function (standard ZIP CRC). */
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c;
  }
  return t;
})();

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}
